// IntakeModal — the new contract request form.
//
// Includes the SF opportunity picker, parent agreement matching UI,
// and associated children selector. These four components are tightly
// coupled and exported together; consumers only need IntakeModal.
//
// Heavy logic: parent rule check (debounced), duplicate detection
// pre-check, AI counterparty validation, document upload + AI
// extraction. All API calls go through apiClient.

import { useState, useEffect, useRef } from "react";
import { T } from "../theme";
import { toTitleCase, fmtDate, hasPermission } from "../utils";
import { apiCall } from "../apiClient";
import { SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import type { UserRow, ContractType } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, SearchBar, EmptyState } from "./primitives";
import { REQUEST_TYPES, PRIORITIES } from "../constants";
import { useClientScope, CLIENT_SCOPE_ALL } from "../contexts/ClientScopeContext";

const SFOppPicker = ({ open = false, onClose = undefined, onSelect = undefined }: { open?: boolean; onClose?: () => void; onSelect?: any }) => {
  const [search, setSearch] = useState("");
  const filtered = SF_OPPORTUNITIES.filter(o => `${o.name} ${o.accountName} ${o.owner}`.toLowerCase().includes(search.toLowerCase()));
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Import from Salesforce Opportunity" w={780}>
      <SearchBar value={search} onChange={setSearch} placeholder="Search opportunities by name, account, or owner..." />
      <div style={{ fontSize: 10, color: T.txD, marginBottom: 10 }}>Select an Opportunity to pre-populate the intake form. Data will be pulled from Salesforce and a persistent link will be stored.</div>
      {filtered.map(opp => {
        const sc = SF_STAGE_COLORS[opp.stage] || { c: T.txM, b: T.bg4 };
        return (
          <Card key={opp.id} onClick={() => { onSelect?.(opp); onClose?.(); }} style={{ marginBottom: 6, cursor: "pointer", borderLeft: `3px solid ${sc.c}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{opp.name}</span>
                  <Badge c={sc.c} b={sc.b}>{opp.stage}</Badge>
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: T.txM, flexWrap: "wrap" }}>
                  <span>{opp.accountName}</span>
                  <span style={{ color: T.txD }}>·</span>
                  <span>${(opp.amount || 0).toLocaleString()}</span>
                  <span style={{ color: T.txD }}>·</span>
                  <span>Close: {fmtDate(opp.closeDate)}</span>
                  <span style={{ color: T.txD }}>·</span>
                  <span>Owner: {opp.owner}</span>
                  <span style={{ color: T.txD }}>·</span>
                  <span>{opp.probability}% prob</span>
                </div>
              </div>
              <a href={sfLink(opp.id)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 10, color: T.acc, textDecoration: "none" }}>View in SF ↗</a>
            </div>
          </Card>
        );
      })}
      {filtered.length === 0 && <EmptyState icon="☁" msg="No matching opportunities" />}
    </Modal>
  );
};

const ParentMatchUI = ({ parentRule = undefined, parentMatches = undefined, selectedParent = undefined, setSelectedParent = undefined, parentIsOnlineTerms = undefined, setParentIsOnlineTerms = undefined, missingParentExplanation = undefined, setMissingParentExplanation = undefined, user = null, overrideBypass = undefined, setOverrideBypass = undefined, onSelectParent = undefined, onlineTermsParty = undefined, setOnlineTermsParty = undefined, onlineTermsUrl = undefined, setOnlineTermsUrl = undefined }: { parentRule?: any; parentMatches?: any; selectedParent?: any; setSelectedParent?: any; parentIsOnlineTerms?: any; setParentIsOnlineTerms?: any; missingParentExplanation?: any; setMissingParentExplanation?: any; user?: UserRow | null; overrideBypass?: any; setOverrideBypass?: any; onSelectParent?: any; onlineTermsParty?: any; setOnlineTermsParty?: any; onlineTermsUrl?: any; setOnlineTermsUrl?: any }) => {
  if (!parentRule) return null;
  // Inline check is tighter than requests.manage_parent_linkage (which also
  // includes contract_manager + commercial_ops) — preserving existing gate.
  const canBypass = ["admin", "legalops", "lawyer"].includes(user?.role || '');
  if (canBypass && overrideBypass) return (
    <div style={{ padding: 10, backgroundColor: T.ylwS, borderRadius: 6, marginBottom: 14, border: `1px solid ${T.ylw}40` }}>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.ylw, fontWeight: 600, cursor: "pointer" }}>
        <input type="checkbox" checked={overrideBypass} onChange={e => setOverrideBypass(e.target.checked)} /> Override: proceeding without parent linkage
      </label>
    </div>
  );
  const hasMatches = parentMatches.length > 0;
  return (
    <div style={{ padding: 14, borderRadius: 8, marginBottom: 14, border: `1px solid ${hasMatches ? T.acc : T.ylw}40`, backgroundColor: hasMatches ? T.accS : T.ylwS }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: hasMatches ? T.acc : T.ylw, marginBottom: 4 }}>{hasMatches ? "Parent Agreement Required" : "No Parent Agreement Found"}</div>
      <div style={{ fontSize: 11, color: T.txM, marginBottom: 10 }}>A <strong>{parentRule.child_type}</strong> requires one of: {parentRule.required_parent_types.join(", ")}</div>
      {hasMatches && parentMatches.map((m: any) => (
        <label key={m.id} onClick={() => { setSelectedParent(m); setParentIsOnlineTerms(false); setMissingParentExplanation(""); if (onSelectParent) onSelectParent(m); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", backgroundColor: selectedParent?.id === m.id ? T.accS : T.bg2, borderRadius: 5, marginBottom: 4, cursor: "pointer", border: `1px solid ${selectedParent?.id === m.id ? T.acc : T.brd}` }}>
          <input type="radio" name="parentSelect" checked={selectedParent?.id === m.id && !parentIsOnlineTerms} readOnly />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: T.tx }}>{m.name || m.title}</div>
            <div style={{ fontSize: 10, color: T.txD }}>{m.contract_type || m.agreement_type} · {m.counterparty_entity || m.counterparty}{m.effective_date ? ` · ${fmtDate(m.effective_date)}` : ""}</div>
          </div>
          <Badge c={m.source === "repository" ? T.grn : T.blu} b={m.source === "repository" ? T.grnS : T.bluS}>{m.source === "repository" ? "Repository" : "In Progress"}</Badge>
        </label>
      ))}
      {parentRule.online_terms_eligible && (
        <label onClick={() => { setParentIsOnlineTerms(true); setSelectedParent(null); setMissingParentExplanation(""); if (setOnlineTermsParty && !onlineTermsParty) setOnlineTermsParty('counterparty'); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", backgroundColor: parentIsOnlineTerms ? "rgba(26,138,158,.08)" : T.bg2, borderRadius: 5, marginBottom: 4, cursor: "pointer", border: `1px solid ${parentIsOnlineTerms ? T.cyn : T.brd}` }}>
          <input type="radio" name="parentSelect" checked={parentIsOnlineTerms} readOnly />
          <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 500, color: T.tx }}>Parent agreement is governed by Online Terms</div><div style={{ fontSize: 10, color: T.txD }}>Pick whose terms govern this agreement; attorney adds the URL before signature</div></div>
          <Badge c={T.cyn} b={T.cynS}>Online Terms</Badge>
        </label>
      )}
      {parentIsOnlineTerms && setOnlineTermsParty && (
        <div style={{ marginLeft: 24, marginTop: 4, marginBottom: 8, padding: "10px 12px", borderRadius: 5, border: `1px solid ${T.cyn}40`, backgroundColor: "rgba(26,138,158,.04)" }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 6, fontWeight: 700 }}>Whose Online Terms govern?</div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.tx, marginBottom: 4, cursor: "pointer" }}>
            <input type="radio" name="onlineTermsParty" checked={onlineTermsParty === 'counterparty'} onChange={() => setOnlineTermsParty('counterparty')} />
            Counterparty's online terms (vendor's OF/SOW under their terms)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.tx, cursor: "pointer" }}>
            <input type="radio" name="onlineTermsParty" checked={onlineTermsParty === 'company'} onChange={() => setOnlineTermsParty('company')} />
            Company's own online terms (customer signed our online terms)
          </label>
          {setOnlineTermsUrl && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 3, fontWeight: 700 }}>Online Terms URL (optional at intake)</div>
              <input value={onlineTermsUrl || ""} onChange={e => setOnlineTermsUrl(e.target.value)} placeholder="https://..." style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
          )}
        </div>
      )}
      {!hasMatches && !parentIsOnlineTerms && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 3, fontWeight: 700 }}>Explain the circumstances (required)</div>
          <textarea value={missingParentExplanation} onChange={e => setMissingParentExplanation(e.target.value)} placeholder="e.g., Parent MSA is being negotiated separately..." rows={2} style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
        </div>
      )}
      {canBypass && <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: T.txD, marginTop: 8, cursor: "pointer" }}><input type="checkbox" checked={overrideBypass} onChange={e => setOverrideBypass(e.target.checked)} /> Override: proceed without parent linkage</label>}
    </div>
  );
};

const AssociatedChildrenUI = ({ associatedChildren = [], selectedAssociatedChildren = [], setSelectedAssociatedChildren = undefined }: { associatedChildren?: any[]; selectedAssociatedChildren?: any[]; setSelectedAssociatedChildren?: any }) => {
  if (!associatedChildren || associatedChildren.length === 0) return null;
  return (
    <div style={{ padding: 14, borderRadius: 8, marginBottom: 14, border: `1px solid ${T.blu}40`, backgroundColor: T.bluS }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.blu, marginBottom: 4 }}>Associated Agreements</div>
      <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>Would you also like to create any of the following?</div>
      {associatedChildren.map((ct: any) => (
        <label key={ct} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.tx, marginBottom: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={selectedAssociatedChildren.includes(ct)} onChange={e => setSelectedAssociatedChildren((prev: any) => e.target.checked ? [...prev, ct] : prev.filter((x: any) => x !== ct))} /> {ct}
        </label>
      ))}
    </div>
  );
};

export const IntakeModal = ({ open = false, onClose = undefined, onSubmit = undefined, user = null, contractTypes = [], prefill = null }: { open?: boolean; onClose?: any; onSubmit?: any; user?: UserRow | null; contractTypes?: ContractType[]; prefill?: any }) => {
  const [requestMode, setRequestMode] = useState<any>(null);
  const [f, setF] = useState<Record<string, any>>({ title: "", agreementCategory: "", type: "", counterparty: "", counterpartyAddress: "", counterpartyContact: "", business_unit: "", department: "", priority: "Normal", targetDate: "", description: "", businessOwner: user?.name || "", business_owner_email: "", estimated_deal_value: "", attachments: [], sfOppId: null, sfOppName: null, sfAmount: null, sfContractTerms: null, sfPaymentTerms: null, client_id: null });

  // Wave 4e: client_id picker — pre-populated and locked from the sidebar
  // ClientScopeContext when the picker has narrowed scope or the user has
  // exactly one available client. Free-choice dropdown otherwise.
  const { availableClients: scopeClients, selected: scopeSelected, canPickAll: scopeCanPickAll, edition: scopeEdition } = useClientScope();
  const lockedClientId: number | null =
    scopeSelected !== CLIENT_SCOPE_ALL ? scopeSelected
      : (scopeClients.length === 1 && !scopeCanPickAll ? scopeClients[0].id
        : null);
  useEffect(() => {
    setF((p: any) => {
      if (lockedClientId != null) return p.client_id === lockedClientId ? p : { ...p, client_id: lockedClientId };
      // free-pick mode: keep current selection if still valid; otherwise clear
      const validIds = new Set(scopeClients.map(c => c.id));
      if (p.client_id != null && validIds.has(Number(p.client_id))) return p;
      return p.client_id == null ? p : { ...p, client_id: null };
    });
  }, [lockedClientId, scopeClients]);
  const [drag, setDrag] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [attachmentFiles, setAttachmentFiles] = useState<any[]>([]);
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [companyEntities, setCompanyEntities] = useState<any[]>([]);
  const [selectedCompanyEntityId, setSelectedCompanyEntityId] = useState<string>("");
  const [parentRule, setParentRule] = useState<any>(null);
  const [parentMatches, setParentMatches] = useState<any[]>([]);
  const [selectedParent, setSelectedParent] = useState<any>(null);
  const [parentIsOnlineTerms, setParentIsOnlineTerms] = useState(false);
  const [onlineTermsParty, setOnlineTermsParty] = useState<'counterparty' | 'company' | null>(null);
  const [onlineTermsUrl, setOnlineTermsUrl] = useState<string>("");
  const [missingParentExplanation, setMissingParentExplanation] = useState("");
  const [showParentRequired, setShowParentRequired] = useState(false);
  const [overrideBypass, setOverrideBypass] = useState(false);
  // EXHIBIT_CASCADE_W4_20260415 — template cascade state
  const [cascadeInfo, setCascadeInfo] = useState<{ templateId: string | null; tier: number | null; explanation: string | null; templateName: string | null }>({ templateId: null, tier: null, explanation: null, templateName: null });
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [associatedChildren, setAssociatedChildren] = useState<any[]>([]);
  const [selectedAssociatedChildren, setSelectedAssociatedChildren] = useState<any[]>([]);
  const [showSFPicker, setShowSFPicker] = useState(false);
  // Duplicate-agreement detection (2026-04-08 feature)
  const [duplicateBlock, setDuplicateBlock] = useState<any>(null); // null | { existing, stage: 'showing'|'escalating', payload }
  const [escalationReason, setEscalationReason] = useState("");
  // Affiliate rights review (4d): when the selected parent has no affiliate_rights
  // or explicitly says "No affiliate rights" AND the child counterparty differs
  // from the parent counterparty, surface an amber warning with an NMI
  // escalation checkbox. Checking it flips the new request into
  // 'needs_more_information' with a structured escalation_reason.
  const [escalateAffiliateReview, setEscalateAffiliateReview] = useState(false);
  // Counterparty validation (2026-04-08 feature, mirrors self-service pattern)
  const [cpValidation, setCpValidation] = useState<any>(null); // null | { legal_name, address, entity_type, jurisdiction, entity_designation, confidence, notes }
  const [cpValidating, setCpValidating] = useState(false);

  useEffect(() => { apiCall('/settings/business-units').then(r => { if (r?.businessUnits) setBusinessUnits(r.businessUnits); });
    apiCall('/company-entities?active_only=true').then(r => { if (r?.entities) { setCompanyEntities(r.entities); const def = r.entities.find((e: any) => e.is_default); if (def && !selectedCompanyEntityId) setSelectedCompanyEntityId(def.id); } }); }, []);

  // Reset all form state when modal opens. If `prefill` is provided (e.g.
  // when launched from the repository "Request Child Agreement" flow), seed
  // the form with the parent's counterparty/category/type and lock the
  // selected parent so the user doesn't need to re-discover it.
  useEffect(() => {
    if (open) {
      setRequestMode(prefill ? 'standard' : null);
      setF({
        title: prefill?.title || "",
        agreementCategory: prefill?.agreementCategory || "",
        type: prefill?.type || "",
        counterparty: prefill?.counterparty || "",
        counterpartyAddress: prefill?.counterpartyAddress || "",
        counterpartyContact: "",
        business_unit: "",
        department: "",
        priority: "Normal",
        targetDate: "",
        description: prefill?.description || "",
        businessOwner: user?.name || "",
        business_owner_email: "",
        estimated_deal_value: "",
        attachments: [],
        sfOppId: null,
        sfOppName: null,
        sfAmount: null,
        sfContractTerms: null,
        sfPaymentTerms: null,
        // Pre-select client from parent contract when launched via + Request Child.
        // Subsequent lockedClientId effect will override only if scope forces it.
        client_id: prefill?.client_id ?? null,
      });
      setAttachmentFiles([]);
      setCpValidation(null);
      setCpValidating(false);
      setParentRule(null);
      setParentMatches([]);
      setSelectedParent(prefill?.parent || null);
      // Seed cascade info from prefill (set by Repository.tsx childRequestPrefill flow)
      if (prefill?.cascadeTier || prefill?.templateId) {
        setCascadeInfo({
          templateId: prefill.templateId || null,
          tier: prefill.cascadeTier || null,
          explanation: prefill.cascadeExplanation || null,
          templateName: prefill.cascadeTemplateName || null,
        });
        setSelectedTemplateId(prefill.templateId || null);
      } else {
        setCascadeInfo({ templateId: null, tier: null, explanation: null, templateName: null });
        setSelectedTemplateId(null);
      }
      setAvailableTemplates([]);
      setParentIsOnlineTerms(false);
      setMissingParentExplanation("");
      setShowParentRequired(false);
      setOverrideBypass(false);
      setAssociatedChildren([]);
      setSelectedAssociatedChildren([]);
      setShowSFPicker(false);
      setExtracting(false);
      setDrag(false);
    }
  }, [open, user?.name, prefill]);

  // EXHIBIT_CASCADE_W4_20260415 — when agreement type OR parent changes, re-resolve cascade (for the non-prefill "New Request" path)
  useEffect(() => {
    if (!open || !f.type) return;
    // If prefill already supplied cascade info AND the user hasn't changed type, keep it
    if (prefill?.templateId && f.type === prefill.type) return;
    const parentContractId = selectedParent?.source === 'repository' ? selectedParent.id : null;
    const CHILD_TYPES = ['Work Order', 'Change Order', 'Statement of Work', 'Order Form'];
    if (!CHILD_TYPES.some(t => t.toLowerCase() === f.type.toLowerCase())) {
      setCascadeInfo({ templateId: null, tier: null, explanation: null, templateName: null });
      return;
    }
    let cancelled = false;
    const url = parentContractId
      ? `/self-service/templates/cascade?agreement_type=${encodeURIComponent(f.type)}&parent_contract_id=${encodeURIComponent(parentContractId)}`
      : `/self-service/templates/cascade?agreement_type=${encodeURIComponent(f.type)}`;
    apiCall(url).then((r: any) => {
      if (cancelled) return;
      if (r && !r.error) {
        setCascadeInfo({
          templateId: r.template?.id || null,
          tier: r.cascade_tier,
          explanation: r.cascade_explanation,
          templateName: r.template?.name || null,
        });
        if (!selectedTemplateId) setSelectedTemplateId(r.template?.id || null);
      }
    }).catch(() => {});
    // Also load all available templates for the override dropdown
    apiCall(`/self-service/templates`).then((r: any) => {
      if (cancelled) return;
      if (r?.templates) {
        const filtered = r.templates.filter((t: any) => t.is_active && (t.agreement_type || '').toLowerCase() === f.type.toLowerCase());
        setAvailableTemplates(filtered);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, f.type, selectedParent?.id]);

  // Parent requirement check
  useEffect(() => {
    if (!f.agreementCategory || !f.type || !f.counterparty || f.counterparty.length < 2) {
      setParentRule(null); setParentMatches([]); setShowParentRequired(false); return;
    }
    const timer = setTimeout(async () => {
      const result = await apiCall('/parent-rules/check', { method: 'POST', body: JSON.stringify({ agreement_category: f.agreementCategory, agreement_type: f.type, counterparty: f.counterparty }) });
      if (result) {
        setParentRule(result.requires_parent ? result.rule : null);
        setParentMatches(result.matches || []);
        setShowParentRequired(result.requires_parent);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [f.agreementCategory, f.type, f.counterparty]);

  // Associated child check
  useEffect(() => {
    if (!f.agreementCategory || !f.type) { setAssociatedChildren([]); return; }
    apiCall('/parent-rules/check-associated', { method: 'POST', body: JSON.stringify({ agreement_category: f.agreementCategory, agreement_type: f.type }) })
      .then(r => setAssociatedChildren(r?.suggest_children ? r.child_types : []));
  }, [f.agreementCategory, f.type]);

  // Counterparty validation (2026-04-08, mirrors self-service /validate-counterparty pattern)
  // Debounced 600ms after user stops typing. Triggers at 3+ chars. Returns Claude's
  // best guess at the canonical legal name + registered address + entity type + jurisdiction.
  useEffect(() => {
    if (!f.counterparty || f.counterparty.trim().length < 3) {
      setCpValidation(null);
      setCpValidating(false);
      return;
    }
    setCpValidating(true);
    const timer = setTimeout(async () => {
      try {
        const result = await apiCall('/self-service/validate-counterparty', {
          method: 'POST',
          body: JSON.stringify({ name: f.counterparty.trim() })
        });
        if (result) setCpValidation(result);
      } catch (err: any) {
        console.error('Counterparty validation failed:', err);
      } finally {
        setCpValidating(false);
      }
    }, 600);
    return () => { clearTimeout(timer); setCpValidating(false); };
  }, [f.counterparty]);

  const tryExtract = async (file: File) => {
    const ext = (file!.name.split('.').pop() || '').toLowerCase();
    if (!file || !['pdf', 'docx', 'doc'].includes(ext)) return;
    setExtracting(true);
    setExtractError('');
    try {
      const base64 = await new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve((reader.result as string).split(',')[1]); reader.onerror = reject; reader.readAsDataURL(file); });
      const data = await apiCall('/extraction/extract-quick', { method: 'POST', body: JSON.stringify({ document_base64: base64, document_type: ext === 'pdf' ? 'pdf' : 'docx' }) });
      if (data) {
        const abbrevMap: Record<string, string> = { MSA: "Master Services Agreement", SOW: "Statement of Work", NDA: "Confidentiality Agreement", DPA: "Data Processing Agreement" };
        const category = data.agreement_category || "";
        const rawType = data.request_type || "";
        const mappedType = abbrevMap[rawType] || rawType;
        const matchedType = contractTypes.find(t => t.category === category && (t.name === mappedType || (t.name || '').toLowerCase().includes(rawType.toLowerCase())));
        setF((prev: any) => ({ ...prev,
          title: data.title && !prev.title ? toTitleCase(data.title) : prev.title,
          counterparty: data.counterparty && !prev.counterparty ? toTitleCase(data.counterparty) : prev.counterparty,
          counterpartyAddress: data.counterparty_address || prev.counterpartyAddress,
          type: matchedType && !prev.type ? matchedType.name : (!prev.type ? rawType : prev.type),
          agreementCategory: category && !prev.agreementCategory ? category : prev.agreementCategory
        }));
      } else {
        setExtractError('AI extraction unavailable \u2014 the service may be temporarily busy. You can fill in the fields manually or try uploading again.');
      }
    } catch (err: any) {
      console.error('Quick extraction failed:', err);
      setExtractError('AI extraction failed. You can fill in the fields manually.');
    } finally { setExtracting(false); }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); setDrag(false); const files = Array.from(e.dataTransfer?.files || []); setF((p: any) => ({ ...p, attachments: [...p.attachments, ...files.map((x: any) => x.name)] })); setAttachmentFiles(prev => [...prev, ...files]); if (files[0]) tryExtract(files[0]); };
  const handleSFSelect = (opp: any) => { setF((p: any) => ({ ...p, title: opp.name, counterparty: opp.accountName, targetDate: opp.closeDate, businessOwner: opp.owner, description: `Imported from Salesforce Opportunity: ${opp.name}\nAccount: ${opp.accountName}\nAmount: $${(opp.amount || 0).toLocaleString()}`, sfOppId: opp.id, sfOppName: opp.name, sfAmount: opp.amount, sfContractTerms: opp.contractTerms, sfPaymentTerms: opp.paymentTerms, type: opp.type === "Renewal" ? "MSA" : opp.type === "Expansion" ? "SOW" : "" })); };

  const submit = async (overridePayload: any = null) => {
    // Defensive: if called from a button onClick={() => { submit(); }} React passes the click
    // event as the first arg. Treat any object with a nativeEvent or preventDefault
    // method as "no override" so the form behaves correctly.
    if (overridePayload && (overridePayload.nativeEvent || typeof overridePayload.preventDefault === 'function')) {
      overridePayload = null;
    }
    if (!f.type || !f.counterparty) return;
    // Description always required
    if (!f.description || !f.description.trim()) {
      alert("Description is required. Please describe the scope and purpose of this request.");
      return;
    }
    // Wave 4e: client selection gating. Firm/law-firm path unchanged — a client
    // must be picked when the user has any available clients. In-house hides the
    // picker entirely (see the scopeEdition gate on the selector below), so there
    // is nothing for the user to select; gating here would wrongly block every
    // in-house submission. In-house instead auto-resolves to the single implicit
    // client when unambiguous, otherwise submits null (POST /requests permits a
    // null client_id for picker-less intake paths).
    if (scopeEdition !== "in_house") {
      if (scopeClients.length > 0 && (f.client_id == null || f.client_id === "")) {
        alert("Please select a client before submitting.");
        return;
      }
      if (scopeClients.length === 0) {
        alert("No client assigned to your account. Contact your administrator.");
        return;
      }
    } else if ((f.client_id == null || f.client_id === "") && scopeClients.length === 1) {
      // Single-grant in-house user: resolve to their one client so the row is
      // scoped correctly (the backend does not auto-scope a null client_id).
      f.client_id = scopeClients[0].id;
    }

    // Counterparty legal address is required (2026-04-08 feature)
    if (!f.counterpartyAddress || !f.counterpartyAddress.trim()) {
      alert("Counterparty legal address is required. The validator may have suggested one — click 'Use this address' to accept it, or type one manually.");
      return;
    }
    // Validate parent requirement
    if (showParentRequired && !overrideBypass && !selectedParent && !parentIsOnlineTerms && !missingParentExplanation.trim()) {
      alert("Please select a parent agreement, designate online terms, or explain why no parent exists.");
      return;
    }
    // Auto-set title from agreement type
    const payload = overridePayload || { ...f, title: f.type || 'Contract Request', attachmentFiles };
    if (!overridePayload) {
      if (selectedParent) {
        if (selectedParent.source === 'repository') payload.parent_contract_id = selectedParent.id;
        else payload.parent_request_id = selectedParent.id;
      }
      if (parentIsOnlineTerms) {
        payload.parent_is_online_terms = true;
        const party = onlineTermsParty || 'counterparty';
        payload.online_terms_party = party;
        payload.governance_type = party === 'company' ? 'company_online_terms' : 'counterparty_online_terms';
        if (onlineTermsUrl && onlineTermsUrl.trim()) payload.online_terms_url = onlineTermsUrl.trim();
      } else if (selectedParent) {
        payload.governance_type = 'negotiated_master';
      }
      if (showParentRequired && !selectedParent && !parentIsOnlineTerms && missingParentExplanation.trim() && !overrideBypass) {
        payload.missing_parent_explanation = missingParentExplanation.trim();
      }
      // Pass through validated counterparty entity type / jurisdiction if the AI validation captured them.
      // counterparty_address is already in payload via { ...f, counterpartyAddress } above.
      if (cpValidation && cpValidation.entity_type) payload.counterpartyEntityType = cpValidation.entity_type;
      if (cpValidation && cpValidation.jurisdiction) payload.counterpartyJurisdiction = cpValidation.jurisdiction;
      // EXHIBIT_CASCADE_W4_20260415 — pass resolved template + agreement_type so /create-request can auto-populate draft
      if (selectedTemplateId) payload.template_id = selectedTemplateId;
      if (f.type) payload.agreement_type = f.type;
      // Affiliate rights NMI escalation (4d): piggybacks on the existing
      // needs_more_information path via the affiliate_rights_escalation
      // flag — backend requests.js derives initialStatus and prefixes the
      // description with the structured reason.
      if (escalateAffiliateReview && selectedParent) {
        const parentCp = selectedParent.counterparty_entity || '';
        const childCp = f.counterparty || '';
        const parentName = selectedParent.name || selectedParent.title || 'parent agreement';
        payload.affiliate_rights_escalation = true;
        payload.affiliate_rights_escalation_reason = `[AFFILIATE RIGHTS REVIEW] Child counterparty ${childCp} differs from parent counterparty ${parentCp}. Parent agreement ${parentName} does not specify affiliate rights.`;
      }
    }

    // Duplicate-agreement detection: pre-check via /check-duplicate unless this is the override path
    if (!payload.force) {
      const dupCheck = await apiCall('/requests/check-duplicate', {
        method: 'POST',
        body: JSON.stringify({ contract_type: f.type, agreement_type: f.type, counterparty: f.counterparty })
      });
      if (dupCheck && dupCheck.duplicate) {
        setDuplicateBlock({ existing: dupCheck.existing, stage: 'showing', payload });
        return; // do NOT proceed with onSubmit; user must Cancel or Escalate
      }
    }

    onSubmit?.(payload);
    // Create associated children after parent request is created
    if (selectedAssociatedChildren.length > 0) {
      setTimeout(async () => {
        // The parent request was just created — find it by title to get the ID
        const reqs = await apiCall('/requests?limit=1&search=' + encodeURIComponent(f.title));
        const parentReq = reqs?.requests?.[0];
        if (parentReq) {
          for (const childType of selectedAssociatedChildren) {
            await apiCall(`/requests/${parentReq.id}/create-associated-child`, { method: 'POST', body: JSON.stringify({ child_type: childType }) });
          }
        }
      }, 1500);
    }
    setF({ title: "", agreementCategory: "", type: "", counterparty: "", counterpartyContact: "", business_unit: "", department: "", priority: "Normal", targetDate: "", description: "", businessOwner: user?.name || "", business_owner_email: "", estimated_deal_value: "", attachments: [], sfOppId: null, sfOppName: null, sfAmount: null, sfContractTerms: null, sfPaymentTerms: null });
    setAttachmentFiles([]); setSelectedParent(null); setParentIsOnlineTerms(false); setMissingParentExplanation(""); setOverrideBypass(false); setSelectedAssociatedChildren([]); setDuplicateBlock(null); setEscalationReason(""); setEscalateAffiliateReview(false); setRequestMode(null); onClose?.();
  };

  // Submit the duplicate-override path: re-runs submit() with force=true and the user's escalation reason.
  // Also inherits the canonical counterparty from the first matched executed contract (or first in-flight
  // match as fallback) so the new request lands under the same canonical name as the pre-existing one,
  // not whatever variant the user typed in the form.
  const submitOverride = () => {
    const reason = (escalationReason || "").trim();
    if (!reason) return;
    const matches = duplicateBlock.existing || [];
    const executedMatch = matches.find((m: any) => m.source === 'executed');
    const inflightMatch = matches.find((m: any) => m.source === 'in_flight');
    const canonicalCounterparty = (executedMatch && executedMatch.counterparty) || (inflightMatch && inflightMatch.counterparty) || duplicateBlock.payload.counterparty;
    const overridePayload = { ...duplicateBlock.payload, counterparty: canonicalCounterparty, force: true, escalation_reason: reason };
    submit(overridePayload);
  };

  const catTypeSelector = (<>
    {contractTypes && contractTypes.length > 0 ? (<>
      <Sel label="Agreement Category" value={f.agreementCategory} onChange={v => setF((p: any) => ({ ...p, agreementCategory: v, type: "" }))} options={["Customer", "Vendor", "Corporate"]} required />
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Agreement Type<span style={{ color: T.red }}> *</span></label>
        <select value={f.type} onChange={e => setF((p: any) => ({ ...p, type: e.target.value }))} disabled={!f.agreementCategory} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: f.type ? T.tx : T.txD, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box", cursor: f.agreementCategory ? "pointer" : "not-allowed", opacity: f.agreementCategory ? 1 : 0.5 }}>
          <option value="">{f.agreementCategory ? "Select agreement type..." : "Select category first..."}</option>
          {contractTypes.filter(t => t.category === f.agreementCategory).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          {f.agreementCategory && <optgroup label="Amendments">{contractTypes.filter(t => t.category === f.agreementCategory).map(t => <option key={`amend-${t.id}`} value={`Amendment to ${t.name}`}>Amendment to {t.name}</option>)}</optgroup>}
        </select>
      </div>
    </>) : <Sel label="Request Type" value={f.type} onChange={v => setF((p: any) => ({ ...p, type: v }))} options={REQUEST_TYPES} required />}
  </>);

  // Mode selection screen
  if (!requestMode) return (
    <Modal open={open} onClose={onClose} title="New Contract Request" w={520}>
      <div style={{ display: "flex", gap: 16, padding: "20px 0" }}>
        <div onClick={() => setRequestMode("with_doc")} style={{ flex: 1, padding: "28px 20px", borderRadius: 8, border: `2px solid ${T.brd}`, textAlign: "center", cursor: "pointer", transition: "all .15s" }} onMouseEnter={e => e.currentTarget.style.borderColor = T.acc} onMouseLeave={e => e.currentTarget.style.borderColor = T.brd}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Request with Document</div>
          <div style={{ fontSize: 11, color: T.txM }}>I have a draft agreement to attach</div>
        </div>
        <div onClick={() => setRequestMode("without_doc")} style={{ flex: 1, padding: "28px 20px", borderRadius: 8, border: `2px solid ${T.brd}`, textAlign: "center", cursor: "pointer", transition: "all .15s" }} onMouseEnter={e => e.currentTarget.style.borderColor = T.acc} onMouseLeave={e => e.currentTarget.style.borderColor = T.brd}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✏</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Request without Document</div>
          <div style={{ fontSize: 11, color: T.txM }}>I need a new agreement drafted</div>
        </div>
      </div>
    </Modal>
  );

  return (
    <Modal open={open} onClose={() => { setRequestMode(null); onClose?.(); }} title="New Contract Request" w={680}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <button onClick={() => setRequestMode(null)} style={{ background: "none", border: "none", color: T.acc, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Back</button>
        <span style={{ fontSize: 11, color: T.txD }}>{requestMode === "with_doc" ? "Request with Document" : "Request without Document"}</span>
      </div>

      {/* Salesforce Import */}
      <div style={{ marginBottom: 16, padding: "10px 14px", backgroundColor: "rgba(0,136,209,.08)", border: "1px solid rgba(0,136,209,.2)", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div><div style={{ fontSize: 12, fontWeight: 700, color: "#0088D1" }}>☁ Salesforce</div><div style={{ fontSize: 10, color: T.txM, marginTop: 2 }}>{f.sfOppId ? `Linked: ${f.sfOppName}` : "Import from Salesforce"}</div></div>
        <Btn v="secondary" sz="sm" onClick={() => setShowSFPicker(true)}>{f.sfOppId ? "Change" : "Import"}</Btn>
      </div>

      {/* Duplicate-agreement detection block (2026-04-08) */}
      {duplicateBlock && (
        <div style={{ marginBottom: 16, padding: "14px 16px", backgroundColor: "rgba(220,80,40,.08)", border: "2px solid rgba(220,80,40,.4)", borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 18 }}>⚠</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#c44" }}>Duplicate agreement detected</span>
          </div>
          <div style={{ fontSize: 12, color: T.txM, marginBottom: 12 }}>
            A valid <strong>{f.type}</strong> for <strong>{f.counterparty}</strong> already exists or is in flight. Review the matches below before deciding to proceed.
          </div>
          <div style={{ marginBottom: 12 }}>
            {duplicateBlock.existing.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: "10px 12px", marginBottom: 6, backgroundColor: T.bg3, borderRadius: 5, border: `1px solid ${T.brd}`, fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: T.tx, marginBottom: 2 }}>
                      {item.source === 'executed' ? item.name : item.title}
                    </div>
                    <div style={{ fontSize: 10, color: T.txM }}>
                      <span style={{ display: "inline-block", padding: "2px 6px", borderRadius: 3, backgroundColor: item.source === 'executed' ? "rgba(40,160,80,.15)" : "rgba(200,140,40,.15)", color: item.source === 'executed' ? "#2a8" : "#c84", fontWeight: 600, marginRight: 6 }}>
                        {item.source === 'executed' ? 'EXECUTED' : 'IN-FLIGHT'}
                      </span>
                      {item.source === 'executed' ? (
                        <>
                          {item.contract_type} · status: {item.status}
                          {item.termination_date ? ` · expires ${String(item.termination_date).slice(0,10)}` : ' · no expiration'}
                        </>
                      ) : (
                        <>
                          {item.request_type || item.agreement_type} · status: {item.status}
                          {item.requester_name ? ` · requested by ${item.requester_name}` : ''}
                          {item.created_at ? ` · ${String(item.created_at).slice(0,10)}` : ''}
                        </>
                      )}
                    </div>
                  </div>
                  <a href={item.source === 'executed' ? ('/?repoContract=' + item.id) : ('/?openRequest=' + item.id)} style={{ fontSize: 11, color: T.acc, textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap" }}>
                    View →
                  </a>
                </div>
              </div>
            ))}
          </div>

          {duplicateBlock.stage === 'showing' && (
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn v="secondary" sz="sm" onClick={() => { setDuplicateBlock(null); setEscalationReason(""); }}>Cancel request</Btn>
              <Btn v="primary" sz="sm" onClick={() => setDuplicateBlock((p: any) => ({ ...p, stage: 'escalating' }))}>Escalate with reason</Btn>
            </div>
          )}

          {duplicateBlock.stage === 'escalating' && (
            <div>
              <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 5, textTransform: "uppercase" }}>Reason for new request despite duplicate</label>
              <textarea
                value={escalationReason}
                onChange={e => setEscalationReason(e.target.value)}
                placeholder="Why does this duplicate need a new request? e.g. new project scope, terminated agreement, different jurisdiction, etc."
                rows={3}
                autoFocus
                style={{ width: "100%", padding: "10px 12px", borderRadius: 5, border: `1px solid ${escalationReason.trim() ? T.brd : T.red}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }}
              />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Btn v="secondary" sz="sm" onClick={() => { setDuplicateBlock(null); setEscalationReason(""); }}>Cancel</Btn>
                <Btn v="primary" sz="sm" onClick={submitOverride} disabled={!escalationReason.trim()}>Submit Escalation</Btn>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attachments (with_doc mode only) */}
      {requestMode === "with_doc" && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 5, textTransform: "uppercase" }}>Attachments</label>
          <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={handleDrop} onClick={() => document.getElementById("intakeFile")?.click()} style={{ border: `2px dashed ${drag ? T.acc : T.brd}`, borderRadius: 6, padding: "20px 12px", textAlign: "center", backgroundColor: drag ? T.accS : T.bg3, cursor: "pointer" }}>
            <input id="intakeFile" type="file" multiple accept=".pdf,.docx,.doc" style={{ display: "none" }} onChange={e => { const files = Array.from((e.target as HTMLInputElement).files || []); setF((p: any) => ({ ...p, attachments: [...p.attachments, ...files.map((x: any) => x.name)] })); setAttachmentFiles(prev => [...prev, ...files]); if (files[0]) tryExtract(files[0]); }} />
            <div style={{ fontSize: 12, color: T.txM }}>Drop files or <span style={{ color: T.acc, fontWeight: 600 }}>browse</span></div>
          </div>
          {extracting && <div style={{ fontSize: 10, color: T.acc, marginTop: 4, fontWeight: 600 }}>Analyzing document...</div>}
          {extractError && !extracting && <div style={{ fontSize: 10, color: T.red, marginTop: 4, fontWeight: 500 }}>{extractError}</div>}
          {f.attachments.length > 0 && <div style={{ marginTop: 6 }}>{f.attachments.map((a: any, i: number) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", backgroundColor: T.bg3, borderRadius: 3, marginBottom: 2, fontSize: 11, color: T.tx }}><span>📄 {a}</span><button onClick={(e: React.MouseEvent) => { e.stopPropagation(); setF((p: any) => ({ ...p, attachments: p.attachments.filter((_: any, j: number) => j !== i) })); }} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer" }}>✕</button></div>)}</div>}
        </div>
      )}

      {/* Client scope selector — Wave 4e; hidden entirely in in-house deployments */}
      {scopeEdition !== "in_house" && (
      <div style={{ marginBottom: 12 }}>
        {scopeClients.length === 0 ? (
          <div style={{ padding: "10px 12px", backgroundColor: "rgba(214,77,77,.06)", border: `1px solid rgba(214,77,77,.25)`, borderRadius: 6, fontSize: 12, color: T.red }}>
            No client assigned. Contact your administrator before submitting a request.
          </div>
        ) : lockedClientId != null ? (
          <div>
            <div style={{ fontSize: 11, color: T.txD, marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>Client</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, backgroundColor: T.accS, color: T.tx, fontSize: 13, fontWeight: 600 }}>
              {scopeClients.find(c => c.id === lockedClientId)?.name || `Client #${lockedClientId}`}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 11, color: T.txD, marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>Client *</div>
            <select
              value={f.client_id == null ? "" : String(f.client_id)}
              onChange={e => setF((p: any) => ({ ...p, client_id: e.target.value === "" ? null : Number(e.target.value) }))}
              required
              style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: f.client_id ? T.tx : T.txD, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
            >
              <option value="">Select a client…</option>
              {scopeClients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>
      )}

      {/* In-house Business Context — rendered at top only for in_house deployments */}
      {scopeEdition === "in_house" && (
        <div style={{ marginBottom: 16, padding: 14, border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg2 }}>
          <div style={{ fontSize: 11, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 10 }}>Business Context</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700 }}>Business Unit<span style={{ color: T.red }}> *</span></div>
              <select value={f.business_unit || ""} onChange={e => setF((p: any) => ({ ...p, business_unit: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: `1px solid ${!f.business_unit ? T.red : T.brd}`, borderRadius: 6, fontSize: 13, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}><option value="">Select...</option>{businessUnits.map((bu: any) => <option key={bu.id} value={bu.name}>{bu.name}</option>)}</select>
            </div>
            <Inp label="Department" value={f.department} onChange={v => setF((p: any) => ({ ...p, department: v }))} />
            <Inp label="Business Owner Name" value={f.businessOwner} onChange={v => setF((p: any) => ({ ...p, businessOwner: v }))} />
            <Inp label="Business Owner Email" value={f.business_owner_email} onChange={v => setF((p: any) => ({ ...p, business_owner_email: v }))} type="email" placeholder="name@company.com" />
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700 }}>Estimated Deal Value</div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.txD, fontSize: 13, pointerEvents: "none" }}>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={f.estimated_deal_value === "" || f.estimated_deal_value == null ? "" : Number(f.estimated_deal_value).toLocaleString("en-US")}
                  onChange={e => { const raw = e.target.value.replace(/[,\s]/g, ""); if (raw === "" || /^\d*\.?\d*$/.test(raw)) setF((p: any) => ({ ...p, estimated_deal_value: raw })); }}
                  placeholder="0"
                  style={{ width: "100%", padding: "10px 12px 10px 24px", border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 13, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form fields */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {catTypeSelector}
        <Inp label="Counterparty Legal Name" value={f.counterparty} onChange={v => setF((p: any) => ({ ...p, counterparty: v }))} required />
        <Sel label="Priority" value={f.priority} onChange={v => setF((p: any) => ({ ...p, priority: v }))} options={PRIORITIES} />
      </div>

      {/* Counterparty validation badge (2026-04-08) — auto-runs against /self-service/validate-counterparty */}
      {cpValidating && f.counterparty && f.counterparty.length >= 3 && (
        <div style={{ marginBottom: 10, fontSize: 11, color: T.acc, fontWeight: 600 }}>Validating counterparty...</div>
      )}
      {cpValidation && !cpValidating && f.counterparty && f.counterparty.length >= 3 && (
        <div style={{ marginBottom: 12, padding: "10px 12px", backgroundColor: cpValidation.confidence === "high" ? "rgba(43,122,62,.06)" : "rgba(208,164,64,.06)", border: `1px solid ${cpValidation.confidence === "high" ? "rgba(43,122,62,.2)" : "rgba(208,164,64,.2)"}`, borderRadius: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
            <Badge c={cpValidation.confidence === "high" ? T.grn : T.ylw} b={cpValidation.confidence === "high" ? T.grnS : T.ylwS}>{cpValidation.confidence} confidence</Badge>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{cpValidation.legal_name || "(no canonical name found)"}</span>
            {cpValidation.legal_name && cpValidation.legal_name !== f.counterparty && (
              <button type="button" onClick={() => setF((p: any) => ({ ...p, counterparty: cpValidation.legal_name }))} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Use this name</button>
            )}
          </div>
          {cpValidation.entity_designation && <div style={{ fontSize: 11, color: T.tx, marginBottom: 2 }}>{cpValidation.entity_designation}</div>}
          {cpValidation.address && (
            <div style={{ fontSize: 11, color: T.txM, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span>{cpValidation.address}</span>
              {cpValidation.address !== f.counterpartyAddress && (
                <button type="button" onClick={() => setF((p: any) => ({ ...p, counterpartyAddress: cpValidation.address }))} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Use this address</button>
              )}
            </div>
          )}
          {cpValidation.notes && <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{cpValidation.notes}</div>}
        </div>
      )}

      {/* Counterparty Legal Address — required field (2026-04-08) */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Counterparty Legal Address<span style={{ color: T.red }}> *</span></label>
        <textarea value={f.counterpartyAddress} onChange={e => setF((p: any) => ({ ...p, counterpartyAddress: e.target.value }))} placeholder="123 Main St, Wilmington, DE 19801, USA" rows={2} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${!f.counterpartyAddress?.trim() ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
        <div style={{ fontSize: 10, color: T.txD, marginTop: 3 }}>{requestMode === "with_doc" ? "Auto-extracted from the uploaded document if present. Edit or override as needed." : "Required. Type manually, or click 'Use this address' on the validation badge above."}</div>
      </div>

      {/* Parent requirement check */}
      {showParentRequired && <ParentMatchUI parentRule={parentRule} parentMatches={parentMatches} selectedParent={selectedParent} setSelectedParent={setSelectedParent} parentIsOnlineTerms={parentIsOnlineTerms} setParentIsOnlineTerms={setParentIsOnlineTerms} missingParentExplanation={missingParentExplanation} setMissingParentExplanation={setMissingParentExplanation} user={user} overrideBypass={overrideBypass} setOverrideBypass={setOverrideBypass} onlineTermsParty={onlineTermsParty} setOnlineTermsParty={setOnlineTermsParty} onlineTermsUrl={onlineTermsUrl} setOnlineTermsUrl={setOnlineTermsUrl} onSelectParent={(m: any) => { const cp = m.source === 'repository' ? (m.counterparty_entity || m.counterparty) : m.counterparty; if (cp) setF((prev: any) => ({ ...prev, counterparty: cp })); }} />}

      {/* Affiliate rights review (4d) */}
      {(() => {
        if (!selectedParent || selectedParent.source !== 'repository') return null;
        const parentCp = (selectedParent.counterparty_entity || '').trim();
        const childCp = (f.counterparty || '').trim();
        if (!parentCp || !childCp) return null;
        if (parentCp.toLowerCase() === childCp.toLowerCase()) return null;
        const ar = (selectedParent.affiliate_rights || '').toLowerCase();
        const missingAffiliate = !ar || ar.includes('no affiliate rights');
        if (!missingAffiliate) return null;
        return (
          <div style={{ padding: 12, marginBottom: 14, backgroundColor: "rgba(245,166,35,.08)", border: `1px solid ${T.orn}40`, borderRadius: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.orn, marginBottom: 6 }}>Affiliate Rights Review Recommended</div>
            <div style={{ fontSize: 11, color: T.tx, lineHeight: 1.5, marginBottom: 8 }}>
              The selected parent agreement does not appear to include affiliate rights. The child counterparty (<strong>{childCp}</strong>) differs from the parent counterparty (<strong>{parentCp}</strong>). This may require LegalOps review.
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.tx, cursor: "pointer" }}>
              <input type="checkbox" checked={escalateAffiliateReview} onChange={e => setEscalateAffiliateReview(e.target.checked)} />
              Escalate to LegalOps for affiliate rights review
            </label>
          </div>
        );
      })()}

      {/* EXHIBIT_CASCADE_W4_20260415 — template cascade banner (Wave 4) */}
      {(cascadeInfo.tier !== null) && (() => {
        const tier = cascadeInfo.tier;
        const bg = tier === 1 ? "rgba(43,122,62,.08)" : tier === 2 ? "rgba(26,138,158,.06)" : "rgba(139,147,168,.06)";
        const border = tier === 1 ? "rgba(43,122,62,.3)" : tier === 2 ? "rgba(26,138,158,.2)" : "rgba(139,147,168,.2)";
        const color = tier === 1 ? "#2B7A3E" : tier === 2 ? "#1A8A9E" : T.txD;
        const label = tier === 1 ? "Counterparty-Specific" : tier === 2 ? "Default Template" : "No Template";
        const canOverride = hasPermission(user, 'requests.manage_parent_linkage') || !!(user && user.role && ["contract_manager", "commercial_ops", "legalops", "lawyer", "admin"].includes(user.role));
        return (
          <div data-deploy-marker="EXHIBIT_CASCADE_W4_20260415" style={{ padding: 12, margin: "10px 0", backgroundColor: bg, border: `1px solid ${border}`, borderRadius: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</span>
            </div>
            <div style={{ fontSize: 12, color: T.tx }}>{cascadeInfo.explanation}</div>
            {canOverride && availableTemplates.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 10, color: T.txD, display: "block", marginBottom: 4, fontWeight: 600 }}>Use a different template</label>
                <select value={selectedTemplateId || ""} onChange={e => setSelectedTemplateId(e.target.value || null)}
                  style={{ width: "100%", padding: "6px 8px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit" }}>
                  <option value="">No template (manual drafting)</option>
                  {availableTemplates.map((t: any) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.source_contract_id ? " (counterparty-specific)" : " (admin default)"}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Inp label="Counterparty Contact" value={f.counterpartyContact} onChange={v => setF((p: any) => ({ ...p, counterpartyContact: v }))} />
        {scopeEdition !== "in_house" && (
          <Inp label="Business Owner" value={f.businessOwner} onChange={v => setF((p: any) => ({ ...p, businessOwner: v }))} />
        )}
        <div style={{ marginBottom: 16 }}>
          {scopeEdition !== "in_house" && (<>
            <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700 }}>Business Unit</div>
            <select value={f.business_unit || ""} onChange={e => setF((p: any) => ({ ...p, business_unit: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 13, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}><option value="">Select...</option>{businessUnits.map((bu: any) => <option key={bu.id} value={bu.name}>{bu.name}</option>)}</select>
            <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700, marginTop: 10 }}>Company Entity</div>
          </>)}
          {scopeEdition === "in_house" && (
            <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, fontWeight: 700 }}>Company Entity</div>
          )}
          <select value={selectedCompanyEntityId} onChange={e => setSelectedCompanyEntityId(e.target.value)} style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 13, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}><option value="">Select...</option>{companyEntities.map((ce: any) => <option key={ce.id} value={ce.id}>{ce.name}{ce.is_default ? " (default)" : ""}</option>)}</select>
        </div>
        <Inp label="Target Execution Date" value={f.targetDate} onChange={v => setF((p: any) => ({ ...p, targetDate: v }))} type="date" />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Description<span style={{ color: T.red }}> *</span></label>
        <textarea value={f.description} onChange={e => setF((p: any) => ({ ...p, description: e.target.value }))} placeholder="Describe the scope and purpose of this agreement..." rows={4} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${!f.description?.trim() ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
      </div>

      {/* Associated children */}
      <AssociatedChildrenUI associatedChildren={associatedChildren} selectedAssociatedChildren={selectedAssociatedChildren} setSelectedAssociatedChildren={setSelectedAssociatedChildren} />

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn v="secondary" onClick={() => { setRequestMode(null); onClose?.(); }}>Cancel</Btn>
        <Btn v="primary" onClick={() => submit()} disabled={!f.type || !f.counterparty || !f.description?.trim() || !f.counterpartyAddress?.trim() || (scopeEdition === "in_house" && !f.business_unit)}>{scopeEdition === "in_house" ? "Send to Legal" : "Submit Request"}</Btn>
      </div>
      <SFOppPicker open={showSFPicker} onClose={() => setShowSFPicker(false)} onSelect={handleSFSelect} />
    </Modal>
  );
};